// src/components/common/Layout/ClientLayout.jsx
import React, { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import { useNotifications } from "../../../context/NotificationContext";
import NotificationDropdown from "../NotificationDropdown";
import LogoutModal from "../modals/LogoutModal";
import { formatPersonName } from "../../../utils/formatters";
import { onboardingApi } from "../../../api/packageApi";
import "./ClientLayout.css";
import "./ClientLayout.mobile.css";

const COLLAPSE_KEY = "client-sidebar-collapsed";

// Pages that run as a full-screen, step-by-step flow on phones
// (bottom tab bar is hidden so the Back / Next bar can take its place).
const FLOW_PATHS = ["/request-store", "/website-onboarding"];

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch {
    return false;
  }
};

const ClientLayout = ({ children }) => {
  const { user, logout, endSupportSession } = useAuth();
  const { unreadCount } = useNotifications();

  const location = useLocation();
  const navigate = useNavigate();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(readCollapsed);
  const [endingSupportSession, setEndingSupportSession] = useState(false);
  const [supportSessionError, setSupportSessionError] = useState("");
  const [brandLogo, setBrandLogo] = useState("");

  const userMenuRef = useRef(null);

  useEffect(() => {
    let isCurrent = true;
    onboardingApi
      .getContractSettings()
      .then((response) => {
        if (isCurrent) {
          const settings = response.data || {};
          setBrandLogo(settings.brandLogo || "");
        }
      })
      .catch((error) => {
        console.error("Could not load the client sidebar brand logo:", error);
      });
    return () => {
      isCurrent = false;
    };
  }, []);

  /* ---------- Close user menu on outside click / Escape ---------- */

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsUserMenuOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isUserMenuOpen]);

  useEffect(() => {
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  /* ---------- Navigation ---------- */

  const navGroups = [
    {
      title: "Workspace",
      items: [
        {
          path: "/dashboard",
          label: "Dashboard",
          short: "Home",
          icon: "fas fa-chart-pie",
        },
        {
          path: "/request-store",
          label: "Request store",
          short: "Store",
          icon: "fas fa-store",
        },
        {
          path: "/website-onboarding",
          label: "Request website",
          short: "Website",
          icon: "fas fa-globe",
        },
      ],
    },
    {
      title: "Account",
      items: [
        {
          path: "/subscription",
          label: "Subscription",
          short: "Plan",
          icon: "fas fa-credit-card",
        },
        {
          path: "/communication",
          label: "Communication",
          short: "Inbox",
          icon: "fas fa-comments",
          badge: unreadCount,
          aliases: ["/support", "/notifications"],
        },
      ],
    },
  ];

  const allItems = navGroups.flatMap((group) => group.items);

  const matchesPath = (paths) =>
    paths.some(
      (p) => location.pathname === p || location.pathname.startsWith(`${p}/`),
    );

  const matchesItem = (item) =>
    matchesPath([item.path, ...(item.aliases || [])]);

  const isFlow = matchesPath(FLOW_PATHS);

  /* ---------- User ---------- */

  const displayName = formatPersonName(user?.FullName, "Client");
  const firstName = displayName.split(" ").filter(Boolean)[0] || "there";

  const getInitials = () => {
    const names = displayName.split(" ").filter(Boolean);
    if (names.length >= 2) return `${names[0][0]}${names[1][0]}`.toUpperCase();
    return (names[0]?.[0] || "C").toUpperCase();
  };

  /* ---------- Page title ---------- */

  const currentItem = allItems.find(matchesItem);
  const currentGroup = navGroups.find((g) => g.items.includes(currentItem));
  const isDashboard = location.pathname === "/dashboard";
  const pageTitle = isDashboard
    ? `Welcome back, ${displayName}`
    : currentItem?.label || "Client portal";
  const pageTitleShort = isDashboard
    ? `Hi, ${firstName}`
    : currentItem?.label || "Client portal";

  /* ---------- Actions ---------- */

  const handleLogout = () => {
    setShowLogoutModal(true);
    setIsUserMenuOpen(false);
  };

  const confirmLogout = () => {
    if (user?.SupportSession) {
      setShowLogoutModal(false);
      handleEndSupportSession();
      return;
    }
    logout();
    navigate("/login");
  };

  const handleEndSupportSession = async () => {
    setEndingSupportSession(true);
    setSupportSessionError("");
    try {
      await endSupportSession();
    } catch (error) {
      setSupportSessionError(
        error.response?.data?.error ||
          "The support session ended locally, but the server could not confirm the audit event.",
      );
    } finally {
      setEndingSupportSession(false);
      window.location.replace("/admin");
    }
  };

  const toggleSidebar = () => {
    setIsSidebarCollapsed((collapsed) => {
      const next = !collapsed;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* storage unavailable — ignore */
      }
      return next;
    });
  };

  /* ---------- Render ---------- */

  return (
    <>
      <div
        className={`dashboard-container ${
          isSidebarCollapsed ? "is-collapsed" : ""
        } ${isFlow ? "is-flow" : ""}`}
      >
        {/* Desktop / tablet sidebar (hidden on phones) */}
        <aside id="client-navigation" className="sidebar">
          <div className="sidebar-brand">
            <Link to="/dashboard" className="sidebar-logo" title="Toora Flex">
              {brandLogo ? (
                <img
                  className="sidebar-logo-image"
                  src={brandLogo}
                  alt="Toora Flex"
                  onError={() => setBrandLogo("")}
                />
              ) : (
                <span className="sidebar-logo-mark">TF</span>
              )}
              <span className="sidebar-logo-text">
                <strong>Toora Flex</strong>
                <span>Client portal</span>
              </span>
            </Link>

            <button
              className="sidebar-toggle"
              type="button"
              onClick={toggleSidebar}
              aria-label={
                isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"
              }
              aria-pressed={isSidebarCollapsed}
            >
              <i
                className={`fas fa-angles-${isSidebarCollapsed ? "right" : "left"}`}
              ></i>
            </button>
          </div>

          <div className="sidebar-scroll">
            <nav className="sidebar-navigation" aria-label="Client">
              {navGroups.map((group) => (
                <div className="sidebar-group" key={group.title}>
                  <h2 className="sidebar-section-title">{group.title}</h2>

                  <ul className="sidebar-list">
                    {group.items.map((item) => {
                      const isActive = matchesItem(item);
                      const hasBadge = item.badge > 0;

                      return (
                        <li key={item.path}>
                          <NavLink
                            to={item.path}
                            className={`sidebar-nav-item ${isActive ? "active" : ""}`}
                            aria-current={isActive ? "page" : undefined}
                            title={isSidebarCollapsed ? item.label : undefined}
                          >
                            <i className={`sidebar-nav-icon ${item.icon}`}></i>
                            <span className="sidebar-nav-label">
                              {item.label}
                            </span>
                            {hasBadge && (
                              <span
                                className="sidebar-nav-badge"
                                aria-label={`${item.badge} unread`}
                              >
                                {item.badge > 99 ? "99+" : item.badge}
                              </span>
                            )}
                          </NavLink>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </aside>

        <main className="main-content">
          {user?.SupportSession && (
            <div className="support-session-banner" role="status">
              <div>
                <strong>Support session active</strong>
                <span>
                  You are using this account as {user.SupportSession.adminName}.
                  Client actions are recorded in the audit log.
                  {user.SupportSession.expiresAt
                    ? ` This session ends at ${new Date(
                        user.SupportSession.expiresAt,
                      ).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}.`
                    : ""}
                </span>
                {supportSessionError && (
                  <span className="support-session-error" role="alert">
                    {supportSessionError}
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={handleEndSupportSession}
                disabled={endingSupportSession}
              >
                {endingSupportSession ? "Ending..." : "End support session"}
              </button>
            </div>
          )}
          <header className="top-bar">
            {/* Phones, flows only: leave the flow */}
            <Link
              to="/dashboard"
              className="top-bar-back"
              aria-label="Back to dashboard"
            >
              <i className="fas fa-chevron-left" aria-hidden="true"></i>
            </Link>

            <div className="top-bar-left">
              <nav className="breadcrumb" aria-label="Breadcrumb">
                <Link to="/dashboard">Client portal</Link>
                {currentGroup && !isDashboard && (
                  <>
                    <i className="fas fa-chevron-right" aria-hidden="true"></i>
                    <span>{currentGroup.title}</span>
                  </>
                )}
              </nav>
              <h1>
                <span className="title-full">{pageTitle}</span>
                <span className="title-short">{pageTitleShort}</span>
              </h1>
            </div>

            <div className="top-bar-actions">
              <NotificationDropdown />
              <div className="client-top-user-container" ref={userMenuRef}>
                {isUserMenuOpen && (
                  <div className="client-top-user-menu" role="menu">
                    <div className="client-top-user-menu-head">
                      <strong>{displayName}</strong>
                      <span>Client account</span>
                    </div>
                    <Link
                      to="/profile"
                      role="menuitem"
                      className="client-top-user-menu-item"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <i className="fas fa-user"></i>
                      <span>Profile</span>
                    </Link>
                    <button
                      type="button"
                      role="menuitem"
                      className="client-top-user-menu-item danger"
                      onClick={handleLogout}
                    >
                      <i className="fas fa-sign-out-alt"></i>
                      <span>Sign out</span>
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="client-top-user"
                  onClick={() => setIsUserMenuOpen((open) => !open)}
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  aria-label={`Client menu for ${displayName}`}
                >
                  <span className="client-top-user-avatar">
                    {getInitials()}
                  </span>
                  <span className="client-top-user-info">
                    <strong>{displayName}</strong>
                    <small>Client</small>
                  </span>
                  <i
                    className={`fas fa-chevron-${isUserMenuOpen ? "up" : "down"}`}
                    aria-hidden="true"
                  ></i>
                </button>
              </div>
            </div>
          </header>

          <div className="page-content">{children}</div>

          <footer className="client-footer">
            <span>
              © {new Date().getFullYear()} Toora Flex. All rights reserved.
            </span>

            <div className="footer-links">
              <a href="/privacy-policy" onClick={(e) => e.preventDefault()}>
                Privacy
              </a>
              <a href="/terms" onClick={(e) => e.preventDefault()}>
                Terms
              </a>
            </div>
          </footer>
        </main>

        {/* Phones: bottom tab bar, like a native app */}
        <nav className="mobile-tabbar" aria-label="Client">
          {allItems.map((item) => {
            const isActive = matchesItem(item);
            const hasBadge = item.badge > 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`mobile-tab ${isActive ? "active" : ""}`}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="mobile-tab-icon">
                  <i className={item.icon} aria-hidden="true"></i>
                  {hasBadge && (
                    <span
                      className="mobile-tab-badge"
                      aria-label={`${item.badge} unread`}
                    >
                      {item.badge > 9 ? "9+" : item.badge}
                    </span>
                  )}
                </span>
                <span className="mobile-tab-label">{item.short}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>

      <LogoutModal
        isOpen={showLogoutModal}
        onClose={() => setShowLogoutModal(false)}
        onConfirm={confirmLogout}
      />
    </>
  );
};

export default ClientLayout;
